import { PublicClientApplication, AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig';

let msalInstance: PublicClientApplication | null = null;
let currentAccount: AccountInfo | null = null;

// Check if auth is configured
export function isAuthConfigured(): boolean {
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  return !!clientId && clientId.length > 0;
}

// Initialize MSAL
export async function initializeAuth(): Promise<void> {
  if (!isAuthConfigured()) {
    console.warn('Azure AD authentication not configured. Running without auth.');
    return;
  }

  msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  // Handle redirect response
  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    currentAccount = response.account;
  } else {
    // Check for existing accounts
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      currentAccount = accounts[0];
    }
  }
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  if (!isAuthConfigured()) return true; // Skip auth if not configured
  return currentAccount !== null;
}

// Get current user
export function getCurrentUser(): AccountInfo | null {
  return currentAccount;
}

// Get user display name
export function getUserDisplayName(): string {
  if (!currentAccount) return '';
  return currentAccount.name || currentAccount.username || '';
}

// Login with redirect
export async function login(): Promise<void> {
  if (!msalInstance) {
    throw new Error('MSAL not initialized');
  }

  try {
    await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
}

// Logout
export async function logout(): Promise<void> {
  if (!msalInstance || !currentAccount) return;

  await msalInstance.logoutRedirect({
    account: currentAccount,
    postLogoutRedirectUri: window.location.origin,
  });
}

// Silently acquire token (for API calls if needed later)
export async function getAccessToken(): Promise<string | null> {
  if (!msalInstance || !currentAccount) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: currentAccount,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(loginRequest);
    }
    return null;
  }
}
